import {AfterViewInit,Component,OnInit} from "@angular/core";
import {TranslateService} from "@ngx-translate/core";
import {Filter,FilterValue,ListView,TypeComparaison,TypeFilter} from "@domain/common/list-view";
import {Prestation} from "@domain/prestation/prestation";
import {PrestationAdminListItemComponent} from "@components/admin/frais/prestations/prestations-liste/prestation-list-item/prestation-admin-list-item.component";
import {Sorting} from "@domain/common/list-view/sorting";
import {PrestationService} from "@services/admin/frais/prestation.service";
import {Observable} from "rxjs";
import {Unite} from "@domain/prestation/unite";
import {finalize} from "rxjs/operators";

/**
 * Onglet prestations
 *
 * @author Guillaume TRAVOT
 * @date 26/04/2024
 */
@Component({
    host: {'data-test-id': 'prestations-liste'},
    templateUrl: './prestations-liste.component.html'
})
export class PrestationsListeComponent implements OnInit,AfterViewInit {
    /** Clé du filtre sur les unités */
    readonly keyFilterUnite = 'idUnite';

    /** Liste des prestations */
    listePrestations: ListView<Prestation,PrestationAdminListItemComponent>;

    /** Liste des unités */
    listeUnite$: Observable<Unite[]> = this.prestationService.listeUnite();

    /**
     * Constructeur
     */
    constructor(private translateService: TranslateService,private prestationService: PrestationService) {
    }

    /**
     * Initialisation
     */
    ngOnInit() {
        //Initialisation de la liste
        this.initListe();
    }

    /**
     * Après initialisation de la vue
     */
    ngAfterViewInit() {
        //Initialisation du filtre sur les unités
        this.fetchUniteFilterValues();
    }

    /**
     * Initialisation de la liste
     */
    private initListe(): void {
        //Initialisation de la liste
        this.listePrestations = new ListView<Prestation,PrestationAdminListItemComponent>({
            uri: `/controller/AdminPrestation/liste`,
            title: this.translateService.instant('admin.frais.prestations.title'),
            component: PrestationAdminListItemComponent,
            isFilter: true,
            defaultOrder: 'libelle',
            listeFilters: [
				{
					clef: 'type',
					title: this.translateService.instant('admin.frais.prestations.type'),
					typeComparaison: TypeComparaison[TypeComparaison.EQUAL],
                    listeValues: this.prestationService.listeTypes
                        .map(val => {
                            return {
                                code: String(val.type.valueOf()),
                                libelle: val.libelle
                            }
                        }),
                    multiple: true,
				},{
					clef: 'typeRemboursement',
					title: this.translateService.instant('admin.frais.prestations.remboursable'),
					typeComparaison: TypeComparaison[TypeComparaison.EQUAL],
                    type: TypeFilter[TypeFilter.BOOLEAN],
				},{
                    clef: this.keyFilterUnite,
                    title: this.translateService.instant('admin.frais.prestations.unite'),
                    typeComparaison: TypeComparaison[TypeComparaison.EQUAL],
                    loading: true,
                    listeValues: [],
                    multiple: true
                },{
                    clef: 'libelle',
                    title: this.translateService.instant('admin.frais.prestations.libelle'),
                    typeComparaison: TypeComparaison[TypeComparaison.LIKE],
                    isDefault: true
                },{
                    clef: 'code',
                    title: this.translateService.instant('admin.frais.prestations.code'),
                    typeComparaison: TypeComparaison[TypeComparaison.LIKE],
                    isDefault: true
                },{
                    clef: 'compte',
                    title: this.translateService.instant('admin.frais.prestations.compte'),
                    typeComparaison: TypeComparaison[TypeComparaison.LIKE],
                    isDefault: true
                },{
                    clef: 'isActif',
                    title: this.translateService.instant('admin.frais.prestations.active'),
                    type: TypeFilter[TypeFilter.BOOLEAN],
                    typeComparaison: TypeComparaison[TypeComparaison.EQUAL],
                    isSelected: true,
                    valeur: 'true'
                }
            ]
        });

        //Persistence des filtres
        this.listePrestations.isPersistFilters = true;
        this.listePrestations.className = 'PrestationsListeComponent';

        //Filtre par défaut de la liste
        this.listePrestations.listeSelectedFilters = [{
            clef: 'isActif',
            title: this.translateService.instant('admin.frais.prestations.active'),
            type: TypeFilter[TypeFilter.BOOLEAN],
            valeur: 'true',
            displayedValeur: this.translateService.instant('filter.valeurOuiNon.true'),
            typeComparaison: TypeComparaison[TypeComparaison.EQUAL],
            isDefault: false
        }];

        //Définition des colonnes de tri
        this.listePrestations.columns = [
            {key: 'libelle', title: this.translateService.instant('admin.frais.prestations.libelle')},
            {key: 'code', title: this.translateService.instant('admin.frais.prestations.code')},
            {key: 'compte', title: this.translateService.instant('admin.frais.prestations.compte')},
            {key: 'isActif', title: this.translateService.instant('admin.frais.prestations.active')}
        ];

        //Ajout du tri de la liste selon l'ordre voulu
        this.listePrestations.sorting = new Sorting(this.listePrestations.columns,"libelle");
    }

    /**
     * Initialisation du filtre sur les unités
     */
    private fetchUniteFilterValues(): void {
        //Récupération du filtre sur les unités
        let filterUnite: Filter = this.listePrestations.listeFilters.find(filter => filter.clef == this.keyFilterUnite);

        //Si le filtre sur les unités est trouvé
        if (filterUnite) {
            //Remplissage de la liste d'unités disponibles à partir de l'observable contenant les valeurs en base
            this.listeUnite$
                .pipe(finalize(() => filterUnite.loading = false))
                .subscribe(listeUnite => {
                    //Vide la liste des unités le cas échéant
                    filterUnite.listeValues.splice(0);

                    //Construction et ajout de la valeur au filtre pour chaque unité
                    listeUnite.forEach(unite => {
                        filterUnite.listeValues.push({
                            code: String(unite.idUnite),
                            libelle: unite.libelle
                        } as FilterValue);
                    });
                });
        }
    }
}